// SPDX-License-Identifier: GPL-3.0-or-later
// Engine-free immutable edition/selection admission and bounded complete-book export planning.
import type { MultikeyOccurrence } from "./multikey";
import type { MultikeyIndex, MultikeyRange } from "./multikey-index";
import type { MaterializationRequest } from "./multikey-materializer";
import {
  MULTIKEY_MATERIALIZER_PROFILE,
  MULTIKEY_MAX_INPUT_BYTES,
  MULTIKEY_MAX_JOB_PAGES,
} from "./multikey-worker-protocol";
import { MULTIKEY_EXPORT_PROFILE } from "./multikey-export-protocol";

/** A complete committed vector for one exact published edition, never desired or persisted intent. */
export interface MultikeyExportSnapshot {
  readonly indexIdentity: string;
  readonly profile: string;
  readonly materializerProfile: string;
  readonly index: MultikeyIndex;
  readonly selections: readonly { readonly layerPrefix: string; readonly offset: number }[];
  readonly changedPages: readonly number[];
}

const hash = (value: string): boolean => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
const invalid = (): never => {
  throw new Error("Invalid multikey export snapshot");
};

/** Freezes the small cloned metadata graph; no PDF bytes or viewer handles enter it. */
function freeze<T>(value: T): T {
  if (value && typeof value === "object") {
    for (const child of Object.values(value)) freeze(child);
    Object.freeze(value);
  }
  return value;
}

/** Captures all occurrence offsets synchronously. The caller must first gate unsettled restoration/work. */
export function captureMultikeyExportSnapshot(
  index: MultikeyIndex,
  indexIdentity: string,
  offsets: ReadonlyMap<MultikeyOccurrence, number>,
): MultikeyExportSnapshot {
  if (
    !hash(indexIdentity) ||
    !hash(index.canonical.identity) ||
    !Number.isSafeInteger(index.canonical.byteLength) ||
    index.canonical.byteLength < 8 ||
    index.canonical.byteLength > MULTIKEY_MAX_INPUT_BYTES ||
    !Number.isSafeInteger(index.pageCount) ||
    index.pageCount < 1 ||
    index.pageCount > 10_000 ||
    index.geometry.length !== index.pageCount ||
    index.occurrences.length > 5000 ||
    !index.ranges.length ||
    index.ranges.length > index.pageCount
  )
    invalid();
  for (const geometry of index.geometry) {
    if (
      geometry.view.length !== 4 ||
      ![...geometry.view, geometry.rotation, geometry.userUnit].every(Number.isFinite) ||
      geometry.view[2] <= geometry.view[0] ||
      geometry.view[3] <= geometry.view[1] ||
      ![0, 90, 180, 270].includes(geometry.rotation) ||
      geometry.userUnit <= 0
    )
      invalid();
  }
  let next = 1;
  for (const range of index.ranges) {
    if (
      range.firstPage !== next ||
      !Number.isSafeInteger(range.lastPage) ||
      range.lastPage < next ||
      range.lastPage > index.pageCount ||
      range.pageCount !== range.lastPage - next + 1 ||
      !hash(range.identity) ||
      !Number.isSafeInteger(range.byteLength) ||
      range.byteLength < 8 ||
      range.byteLength > MULTIKEY_MAX_INPUT_BYTES
    )
      invalid();
    next = range.lastPage + 1;
  }
  if (next !== index.pageCount + 1) invalid();
  const seen = new Set<string>();
  const pages = new Set<number>();
  const selections = index.occurrences.map(occurrence => {
    const offset = offsets.get(occurrence);
    if (
      !Number.isSafeInteger(offset) ||
      offset! < 0 ||
      offset! > 11 ||
      typeof occurrence.layerPrefix !== "string" ||
      occurrence.layerPrefix.length > 700 ||
      !/^ulsbs:transpose:v1:id:.+$/.test(occurrence.layerPrefix) ||
      seen.has(occurrence.layerPrefix) ||
      occurrence.layerPageIndices.length > index.pageCount ||
      new Set(occurrence.layerPageIndices).size !== occurrence.layerPageIndices.length
    )
      invalid();
    seen.add(occurrence.layerPrefix);
    for (const page of occurrence.layerPageIndices) {
      if (!Number.isSafeInteger(page) || page < 0 || page >= index.pageCount) invalid();
      if (offset !== 0) pages.add(page + 1);
    }
    return { layerPrefix: occurrence.layerPrefix, offset: offset! };
  });
  return freeze({
    indexIdentity,
    profile: MULTIKEY_EXPORT_PROFILE,
    materializerProfile: MULTIKEY_MATERIALIZER_PROFILE,
    index: structuredClone(index),
    selections,
    changedPages: [...pages].sort((a, b) => a - b),
  });
}

/** Revalidates untrusted worker metadata and creates a private immutable copy. */
export function validateMultikeyExportSnapshot(
  value: MultikeyExportSnapshot,
): MultikeyExportSnapshot {
  if (
    value.profile !== MULTIKEY_EXPORT_PROFILE ||
    value.materializerProfile !== MULTIKEY_MATERIALIZER_PROFILE ||
    value.selections.length !== value.index.occurrences.length
  )
    invalid();
  const offsets = new Map(
    value.index.occurrences.map((o, i) => {
      const selection = value.selections[i]!;
      if (selection.layerPrefix !== o.layerPrefix) invalid();
      return [o, selection.offset] as const;
    }),
  );
  const snapshot = captureMultikeyExportSnapshot(value.index, value.indexIdentity, offsets);
  if (JSON.stringify(value.changedPages) !== JSON.stringify(snapshot.changedPages)) invalid();
  return snapshot;
}

/** Streams small range-local batches, including every family sharing any requested page. */
export function* multikeyExportBatches(snapshot: MultikeyExportSnapshot): Generator<{
  range: MultikeyRange;
  request: MaterializationRequest;
}> {
  for (const range of snapshot.index.ranges) {
    const pages = snapshot.changedPages.filter(p => p >= range.firstPage && p <= range.lastPage);
    for (let start = 0; start < pages.length; start += MULTIKEY_MAX_JOB_PAGES) {
      const batch = pages.slice(start, start + MULTIKEY_MAX_JOB_PAGES);
      const prefixes = new Set(
        snapshot.index.occurrences
          .filter(o => o.layerPageIndices.some(p => batch.includes(p + 1)))
          .map(o => o.layerPrefix),
      );
      yield {
        range,
        request: {
          pages: batch,
          selections: snapshot.selections.filter(s => prefixes.has(s.layerPrefix)),
        },
      };
    }
  }
}
